/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\openvpnClientApi.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the openvpn-client module.
────────────────────────────────────────────────────────────────*/


import type { OpenVPNConnection, UploadConfigRequest } from '../types';

const API_BASE = '/api/network/openvpn-client';

function getApiHeaders(tenantSlug?: string, userId?: string): HeadersInit {
    const headers: HeadersInit = {
        'Content-Type': 'application/json'
    };
    if (tenantSlug) {
        headers['X-Tenant-Slug'] = tenantSlug;
    }
    if (userId) {
        headers['X-User-Id'] = userId;
    }
    return headers;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
}

/**
 * Get connection status and configuration
 */
export async function getConnection(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<OpenVPNConnection>> {
    const response = await fetch(`${API_BASE}/connection`, {
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Upload .ovpn configuration
 */
export async function uploadConfig(
    data: UploadConfigRequest,
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<OpenVPNConnection>> {
    const response = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: getApiHeaders(tenantSlug, userId),
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Connect to VPN
 */
export async function connect(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<OpenVPNConnection>> {
    const response = await fetch(`${API_BASE}/connect`, {
        method: 'POST',
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Disconnect from VPN
 */
export async function disconnect(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<OpenVPNConnection>> {
    const response = await fetch(`${API_BASE}/disconnect`, {
        method: 'POST',
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Delete configuration
 */
export async function deleteConfig(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<{ success: boolean }>> {
    const response = await fetch(`${API_BASE}/config`, {
        method: 'DELETE',
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Refresh status
 */
export async function refreshStatus(
    tenantSlug?: string,
    userId?: string
): Promise<ApiResponse<OpenVPNConnection>> {
    const response = await fetch(`${API_BASE}/refresh`, {
        method: 'POST',
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}
